import React, { useEffect, useState } from "react"
import { withErrorBoundary } from "react-error-boundary"
import { useDisclosure } from "@chakra-ui/react"
import type { PurchaseType } from "components/Shop/PDPPage/types"
import { useCurrencySymbol, useFreeTrial, useLocale } from "hooks"
import { addBasketItemsWithSpinner, getMembershipWithId } from "lib/api"
import { Logger } from "lib/logger"
import type {
  MembershipOptionsV2,
  PricingToggle,
  ShippingType,
  VariantType,
} from "types"

import { useVariantIds } from "hooks/useVariantIds"
import { useUser } from "lib/user"
import { useSegment } from "tracking"
import { navigateTo } from "utils/navigateTo"

import { useTypicalPrice } from "hooks/useTypicalPrice"
import { useUSJourney } from "packages/acquisition/hooks/useUSJourney"

import {
  RadioButton,
  RadioButtonGroup,
  RadioLabel,
} from "packages/ui-shared/forms/RadioButton"
import { useInView } from "react-intersection-observer"
import { formatPrice } from "utils/formatPrice"

import { radioContainer } from "./Pricing.css"
import { PricingModal } from "./PricingModal"
import { ProductActions } from "./ProductActions"
import {
  acquisitionKitsMap,
  getShowAccessPassPopup,
  setShowAccessPassPopup,
} from "./utils"

type Props = {
  variant: VariantType
  setConfirmation: React.Dispatch<React.SetStateAction<boolean>>
  purchaseType: PurchaseType
  setPurchaseType: React.Dispatch<React.SetStateAction<PurchaseType>>
  shippingInfo?: ShippingType[]
  pricingToggle?: PricingToggle
  pdpMembershipOptions?: MembershipOptionsV2[]
  membershipInBag?: boolean
}

export const convertV2MembershipToBasketItem = (
  membership?: MembershipOptionsV2
) => {
  if (typeof membership === "undefined") {
    return
  }
  return getMembershipWithId(membership.sku)
}

const PricingComponent: React.FC<Props> = ({
  variant,
  setConfirmation,
  purchaseType,
  setPurchaseType,
  shippingInfo,
  pricingToggle,
}) => {
  const { hasMembership } = useUser()
  const { freeTrialMembershipId } = useFreeTrial()
  const locale = useLocale()
  const { giftMembershipProductId } = useVariantIds()
  const isGiftMembership = variant.id === giftMembershipProductId
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { track } = useSegment()

  const { ref, inView } = useInView({
    threshold: 1,
  })

  const [selectedMembership] = useState<string | undefined>(
    pricingToggle?.data.defaultPrice
  )

  const [pricingViewed, setPricingViewed] = useState(false)

  const { pricing } = variant
  const currencySymbol = useCurrencySymbol()
  const { typicalPrice } = useTypicalPrice(pricing)
  const isUSJourney = useUSJourney()
  const acquisitionMembershipId = acquisitionKitsMap[locale]?.[variant?.sku]

  let radioLabelInfo = isUSJourney
    ? "With free 60-day trial membership"
    : "Save more with a FREE 60-Day Trial"

  if (
    acquisitionMembershipId &&
    acquisitionMembershipId.startsWith("BP_3M_AUTOUPGRADES")
  ) {
    radioLabelInfo = "With your 3 month membership included"
  }

  if (
    acquisitionMembershipId &&
    acquisitionMembershipId.startsWith("BP_PLUS")
  ) {
    radioLabelInfo = "With your Beauty Pie+ membership included"
  }

  const onRadioChange = (value: string) => {
    if (!getShowAccessPassPopup() && value.startsWith("non-member")) {
      onOpen()
    }
    setPurchaseType(value.startsWith("non-member") ? "non-member" : "member")

    track({
      event: "Page Element Clicked",
      properties: {
        module_content: value.startsWith("non-member")
          ? "Non-member price button"
          : "Member price button",
      },
    })
  }

  const selectedPriceOption = pricingToggle?.data.prices.find(
    (priceOption) => priceOption.id === selectedMembership
  )

  const selectedMembershipSku = freeTrialMembershipId

  const membershipToUse: string =
    acquisitionKitsMap?.[locale]?.[variant.sku] ?? selectedMembershipSku

  const membership = getMembershipWithId(membershipToUse)

  const onAddToBag = async ({
    includeAccessPass,
    purchaseTypeIdentifier,
    membershipOverride,
  }: {
    includeAccessPass: boolean
    purchaseTypeIdentifier: PurchaseType
    membershipOverride?: MembershipOptionsV2
  }) => {
    if (isGiftMembership) {
      await addBasketItemsWithSpinner([
        {
          quantity: 1,
          variantId: giftMembershipProductId,
          variantSku:
            locale === "en-US" ? "BP_PLUS_GIFT_US" : "BP_PLUS_GIFT_UK",
          type: "product",
          origin: "user",
        },
      ]).then(() => navigateTo("/basket"))

      return
    }

    setShowAccessPassPopup(purchaseTypeIdentifier === "non-member" ? "1" : "0")

    const item = {
      quantity: 1,
      variantId: variant.id,
      variantSku: variant.sku,
      type: "product",
      origin: "user",
    } as const

    const priceMembership =
      selectedPriceOption?.price && "membership" in selectedPriceOption.price
        ? selectedPriceOption?.price.membership
        : null

    const membershipSku = membershipOverride?.sku || priceMembership?.sku

    const getMembershipAsBasketItem = (sku?: string) => {
      return {
        ...(convertV2MembershipToBasketItem(membershipOverride) || membership),
        membershipId: sku,
        variantSku: sku,
      }
    }

    const openGateMembership = getMembershipAsBasketItem(membershipSku)
    const membershipWithSku = membershipSku ? openGateMembership : membership

    if (includeAccessPass && !hasMembership) {
      await addBasketItemsWithSpinner(
        [item, membershipWithSku],
        "page",
        "pdp-header",
        { explicitUserMembershipAdd: true }
      )
      setPurchaseType("member")
      setConfirmation(true)
      return
    }

    await addBasketItemsWithSpinner([item], "page", "pdp-header")
    setPurchaseType("non-member")
    setConfirmation(true)
  }

  useEffect(() => {
    if (inView && !pricingViewed) {
      setPricingViewed(true)
    }
  }, [inView, pricingViewed])

  useEffect(() => {
    if (isUSJourney) {
      setPurchaseType("non-member")
    }
  }, [isUSJourney, setPurchaseType])

  return (
    <>
      {!hasMembership && !isGiftMembership && (
        <>
          {!isUSJourney && (
            <PricingModal
              isOpen={isOpen}
              onClose={onClose}
              onAddToBag={onAddToBag}
            />
          )}

          <RadioButtonGroup ref={ref} styles={radioContainer}>
            <RadioButton
              name="pricing-options"
              value="non-member"
              checked={purchaseType === "non-member"}
              onChange={onRadioChange}
            >
              <RadioLabel
                label={isUSJourney ? "Shop now" : "Buy at Non-Member Price"}
                inlineText={formatPrice(typicalPrice, currencySymbol)}
                info={isUSJourney ? "" : "Save with no membership required"}
              />
            </RadioButton>
            <RadioButton
              name="pricing-options"
              value="member"
              checked={purchaseType === "member"}
              onChange={onRadioChange}
            >
              <RadioLabel
                label={`${
                  isUSJourney
                    ? "Shop at Beauty Pie+ Price"
                    : "Buy at Beauty Pie Member Price"
                }`}
                inlineText={formatPrice(pricing.members, currencySymbol)}
                info={radioLabelInfo}
              />
            </RadioButton>
          </RadioButtonGroup>
        </>
      )}
      <ProductActions
        variant={variant}
        onAddToBag={onAddToBag}
        purchaseType={purchaseType}
        shippingInfo={shippingInfo}
      />
    </>
  )
}

export const Pricing = withErrorBoundary(PricingComponent, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`Pricing failed to load. ${error}`)
  },
})
